import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AnchorEntitlements } from "@/lib/entitlements/anchorsClient";

/**
 * Per-mode anchor-entitlement requirement (the client-side counterpart to the
 * server-side gates in `server/entitlementGate.ts`).
 *
 *   - `null` — base-gated; no anchor needed. Used for modes every subscriber
 *     can see (Sales bucket, Tenant Admin, Platform).
 *   - `"turbo_sales_only"` — Turbo Sales anchor AND NOT Turbo Manufacturing.
 *     OI-5b slim subscriber/dealer portal (Q1): Place Order + Order History
 *     only, for dealer orgs that are not also manufacturers.
 *   - `"turbo_manufacturing"` — requires the Mfg anchor; the legacy
 *     `manufacturing_suite` row satisfies this (handled at the
 *     `AnchorEntitlements` shape level — the server endpoint already folds
 *     the alias into the boolean).
 *   - `"any_anchor"` — passes if the org has either anchor. Used for the
 *     embedded layers (Ops + Logistics) per spec §6.3.
 *
 * Spec: `docs/design/admin_split_and_entitlements.md` §4 + §6.3.
 *
 * Values:
 *   - All Mfg + Production modes -> `turbo_manufacturing` (every current
 *     entitled org has this via the legacy alias; no behavior change).
 *   - All Logistics + Ops modes -> `any_anchor` (matches the server-side
 *     `assertAnyAnchorEntitlementActive` helper).
 *   - Manager-bucket modes -> `any_anchor`. This is the **permanent
 *     product decision** (set 2026-06-15): both Sales-anchor customers
 *     (KB authors, training authors, ads-guidance authors) and Mfg-anchor
 *     customers (Cherokee-style manufacturers publishing scenarios to
 *     dealers via `is_sharable=true`, authoring shared ads guidance for
 *     their dealer network) need access to the Manager-bucket authoring
 *     surfaces. The Manager bucket is the cross-anchor "authoring
 *     workbench" — gated by anchor subscription (any one suffices), not
 *     by which anchor.
 *   - Sales bucket modes, Tenant Admin, Platform -> `null` (base-gated).
 */

export type AnchorRequirement =
  | "turbo_sales"
  | "turbo_manufacturing"
  | "turbo_sales_only"
  | "any_anchor"
  | "coverage"
  | null;

export const MODE_ANCHOR_REQUIREMENT: Record<PrimaryMode, AnchorRequirement> = {
  leads: null,
  // Sales-bucket Order Intake panel (post-2026-06-15 bucket move per
  // docs/products/turbo_sales.md §5). `any_anchor` — the mode hosts
  // three role-scoped variants serving both anchor sides (Sales-rep
  // and Ops-reviewer variants for Turbo Sales subscribers; the
  // Mfg-accepter variant for Turbo Manufacturing subscribers, who saw
  // the same content at the pre-move `mfgOrderIntake` location). The
  // surface is deal-submission content that depends on at least one
  // anchor entitlement, not the universal-Sales-bucket framing of
  // `leads` / `salesConsole`.
  salesIntake: "any_anchor",
  // OI-5b slim subscriber/dealer portal (Q1) — turbo_sales without mfg.
  dealerPlaceOrder: "turbo_sales_only",
  dealerOrderHistory: "turbo_sales_only",
  salesConsole: null,
  // Opportunity Engine (Phase 1, 2026-06-15). The reusable opportunity /
  // pricing / documents / generated-PDF surface for the sales side.
  // Spec: docs/GPT_To_Repo.md + docs/design/sql_schema_opportunity.md.
  // Gated on the Turbo Sales anchor specifically (not `any_anchor`)
  // because the engine is the sales-side workflow per the doc; a
  // manufacturer-only org without a sales anchor doesn't get the
  // Opportunity workspace. (Mfg-side users at a sales-anchored org
  // still see it via the OPPORTUNITIES_ROLES `mfg_accepter` admission
  // so they can review opportunities their plant will fulfill.)
  opportunities: "turbo_sales",
  // Documents Editor — baseline Manager (`any_anchor`). Hosts org-wide
  // document authoring plus deal-shaped Proposals sub-mode.
  formsAndFormats: "any_anchor",
  adsAssistant: null,
  training: null,

  locationTools: "any_anchor",
  knowledgebase: "any_anchor",
  aiPrompting: "any_anchor",
  trainingAuthor: "any_anchor",
  adsGuidance: "any_anchor",
  audienceData: "any_anchor",

  admin: null,
  dashboard: null,

  platform: null,
  dev: null,

  mfgInventory: "turbo_manufacturing",
  mfgModels: "turbo_manufacturing",
  mfgPricing: "turbo_manufacturing",
  mfgOverhead: "turbo_manufacturing",
  mfgOrders: "turbo_manufacturing",
  mfgPlants: "turbo_manufacturing",
  mfgBuildPipeline: "turbo_manufacturing",
  mfgLaborBilling: "turbo_manufacturing",
  mfgReports: "turbo_manufacturing",
  mfgAdminHome: "turbo_manufacturing",

  mfgFulfill: "turbo_manufacturing",
  mfgAudits: "turbo_manufacturing",
  mfgInventoryCount: "turbo_manufacturing",
  mfgPurchasing: "turbo_manufacturing",
  mfgWasteAssistant: "turbo_manufacturing",
  mfgLaborPay: "turbo_manufacturing",
  mfgBuilderAssistant: "turbo_manufacturing",
  mfgQcDashboard: "turbo_manufacturing",

  // Fulfillment bucket — baseline (no anchor required) per
  // docs/planning/sales_fulfillment_bucket.md. Pilot orgs (Bennett +
  // ShedCor) get visibility via the `fulfillmentPilotEnabled()`
  // scaffold flag in Slice 1, replaced by a `has_sales_floor`
  // capability column in a follow-on slice. Anchor gate stays open
  // here; pilot gating happens inside the bucket's own feature flag.
  fulfillmentInventory: null,
  fulfillmentOrders: null,
  fulfillmentIntake: null,
  fulfillmentForms: null,
  fulfillmentEntry: null,

  operations: "any_anchor",
  dealTypes: "any_anchor",
  opsLookup: "any_anchor",
  opsObligations: "any_anchor",
  rtoTerms: "any_anchor",
  dailyChecklist: "any_anchor",
  physicalInventory: "any_anchor",

  logisticsRoutes: "any_anchor",
  logisticsCalendar: "any_anchor",
  logisticsCarriers: "any_anchor",

  serviceBoard: "any_anchor",
  serviceRequests: "any_anchor",
  serviceScheduling: "any_anchor",
  serviceDefinitions: "any_anchor",
  serviceMaterialsUsage: "any_anchor",

  // Paid Coverage add-on. Either anchor, plus anchors.coverage.
  coverageAssistant: "coverage",
};

/**
 * Per-mode entitlement gate. Pure function over the requirement + the loaded
 * anchor set.
 *
 * Loading semantics: `anchors === null` returns `true` (treat as open) so the
 * UI doesn't flicker. Once anchors load, the filter applies. This means a
 * brief window after sign-in where a mode that the user lacks the entitlement
 * for is visible; the server-side gate still rejects API calls during this
 * window, and the UI mode visibility tightens as soon as the fetch resolves.
 */
export function canAccessByAnchor(
  mode: PrimaryMode,
  anchors: AnchorEntitlements | null
): boolean {
  const requirement = MODE_ANCHOR_REQUIREMENT[mode];
  if (requirement === null) return true;
  if (anchors === null) return true;
  if (requirement === "any_anchor") {
    return anchors.turbo_sales || anchors.turbo_manufacturing;
  }
  if (requirement === "turbo_sales") return anchors.turbo_sales;
  if (requirement === "turbo_manufacturing") return anchors.turbo_manufacturing;
  if (requirement === "turbo_sales_only") {
    return anchors.turbo_sales && !anchors.turbo_manufacturing;
  }
  if (requirement === "coverage") {
    return (anchors.turbo_sales || anchors.turbo_manufacturing) && anchors.coverage;
  }
  return true;
}
